import React from "react";
import { useNavigate } from "react-router-dom";
import { getFeatureGate } from "../config/featureAccess";
import { useAuth } from "../contexts/AuthContext";
import { useSubscription } from "../providers/SubscriptionProvider";

interface ComingSoonProps {
  featureKey?: string;
  title?: string;
  description?: string;
  className?: string;
  buttonLabel?: string;
  showButton?: boolean;
}

const ComingSoon: React.FC<ComingSoonProps> = ({
  featureKey,
  title,
  description,
  className = "",
  buttonLabel,
  showButton = true,
}) => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { isDefault, isPlusOrHigher } = useSubscription();

  const gate = featureKey ? getFeatureGate(featureKey) : undefined;

  const resolvedTitle = title ?? gate?.title ?? "Unlock Your Full Potential";
  const resolvedDescription =
    description ??
    gate?.description ??
    "This feature is available on a paid plan. Upgrade your subscription to unlock it.";

  const resolvedButtonLabel = (() => {
    if (buttonLabel) return buttonLabel;
    if (isDefault) {
      return userData?.hasUsedTrial ? "Upgrade to Premium" : "Start 14 Day Free Trial";
    }
    if (isPlusOrHigher) {
      return "Upgrade to Premium";
    }
    return "Upgrade Plan";
  })();

  return (
    <div
      className={`max-w-lg mx-auto p-8 mb-4 px-8 flex flex-col gap-3 rounded-xl bg-gradient-to-br from-primary to-blue-800 text-white`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex justify-between">
            <p className="font-bold text-xl font-heading">{resolvedTitle}</p>
            <svg
              className="h-6 w-6 opacity-70 flex-shrink-0 mt-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <p className="text-sm mt-1 font-geist">{resolvedDescription}</p>
        </div>
      </div>

      {showButton && (
        <div className="w-full">
          <button
            onClick={() => navigate("/settings/subscription")}
            className="rounded-full w-full bg-lime text-black p-2 px-6 font-grotesk font-bold"
          >
            {resolvedButtonLabel}
          </button>
        </div>
      )}
    </div>
  );
};

export default ComingSoon;